import React, { useContext, useEffect, useState } from "react";
import {
  CContainer,
  CHeader,
  CHeaderBrand,
  CHeaderNav,
  CDropdown,
  CDropdownToggle,
  CAvatar,
  CDropdownMenu,
  CDropdownHeader,
  CDropdownItem,
  CDropdownDivider,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
} from "@coreui/react";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import LocalizedStrings from "react-localization";
import { FiMenu } from "react-icons/fi";
import {
  AccountSettings,
  Flex,
  Loader,
  ScrollView,
} from "@aws-amplify/ui-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { API, Auth, Storage } from "aws-amplify";

import CimaLogo from "static/images/CimaIoTBlank.png";
import DefaultPhoto from "static/images/DefaultPhoto.png";
import { LayoutContext } from "./context/LayoutContext";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import UserForm from "components/users/UserForm";
import { updateUser } from "graphql/mutations";

const Header = () => {
  const { layoutState, onToggleSidebar, me, setMe } = useContext(LayoutContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modalProfile, setModalProfile] = useState({ visible: false });
  const [modalChangePass, setModalChangePass] = useState({ visible: false });
  const [photoSrc, setPhotoSrc] = useState(DefaultPhoto);
  const [photoSrcLoading, setPhotoSrcLoading] = useState(false);

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await Auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSuccessChangePass = () => {
    toast.success(strings.passwordUpdated);
    setModalChangePass({ visible: false });
  };

  async function onProfileSubmit(values) {
    try {
      const {
        name,
        birthDate,
        position,
        sex,
        gender,
        phones,
        additionalEmails,
        addresses,
        photo,
      } = values;

      setLoading(true);

      await API.graphql({
        query: updateUser.replaceAll("__typename", ""),
        variables: {
          input: {
            id: me.id,
            name,
            birthDate,
            position,
            sex,
            gender,
            phones,
            additionalEmails,
            addresses,
          },
        },
      });

      setMe({
        ...me,
        name,
        birthDate,
        position,
        sex,
        gender,
        phones,
        additionalEmails,
        addresses,
      });

      if (photo) {
        setPhotoSrcLoading(true);
        const fileExtension = photo.name.split(".").pop();
        const fileName = `${me.id}-profile-photo.${fileExtension}`;

        await Storage.put(`profile-photos/${fileName}`, photo, {
          contentType: photo.type,
          level: "public",
        });
        await API.graphql({
          query: updateUser.replaceAll("__typename", ""),
          variables: {
            input: {
              id: me.id,
              photoSrc: fileName,
            },
          },
        });
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoSrc(reader.result);
          setPhotoSrcLoading(false);
        };
        reader.readAsDataURL(photo);
      }

      toast.success(strings.profileUpdated);
      setModalProfile({ visible: false });
      setLoading(false);
    } catch (err) {
      toast.error(strings.responses[err]);
      setLoading(false);
    }
  }

  const getPhotoImage = async (imageKey) => {
    try {
      setPhotoSrcLoading(true);
      const res = await Storage.get(`profile-photos/${imageKey}`, {
        level: "public",
      });
      setPhotoSrc(res);
      setPhotoSrcLoading(false);
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  };

  useEffect(() => {
    if (me.photoSrc) getPhotoImage(me.photoSrc);
  }, [me.photoSrc]);

  if (me.id && me.permissions?.length <= 0) navigate("/permissions-required");

  return (
    <>
      <CHeader position="sticky" className="mb-4" style={styles.header}>
        <CContainer fluid>
          <CHeaderBrand style={styles.brand}>
            <Flex style={styles.containerFlex}>
              <FiMenu
                style={styles.iconMenu}
                onClick={() => onToggleSidebar(!layoutState.sidebarVisible)}
              />
              <img src={CimaLogo} alt="Cima Logo" style={styles.logo} />
              {strings.headerTitle}
            </Flex>
          </CHeaderBrand>
          <CHeaderNav>
            <CDropdown variant="nav-item">
              {photoSrcLoading ? (
                <Loader />
              ) : (
                <CDropdownToggle
                  placement="bottom-end"
                  className="py-0 pe-0"
                  caret={false}
                >
                  <CAvatar src={photoSrc} size="small" />
                </CDropdownToggle>
              )}
              <CDropdownMenu className="pt-0" placement="bottom-end">
                <CDropdownHeader className="bg-body-secondary fw-semibold mb-2 rounded-top">
                  {strings.settings}
                </CDropdownHeader>
                <CDropdownItem
                  role="button"
                  className="d-inline-flex align-items-center"
                  onClick={() => setModalProfile({ visible: true })}
                >
                  <PersonIcon
                    color="action"
                    fontSize="small"
                    className="me-1"
                  />{" "}
                  {strings.profile}
                </CDropdownItem>
                {!me.authProvider && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center"
                    onClick={() => setModalChangePass({ visible: true })}
                  >
                    <LockIcon
                      color="action"
                      fontSize="small"
                      className="me-1"
                    />{" "}
                    {strings.changePassword}
                  </CDropdownItem>
                )}
                <CDropdownDivider />
                <CDropdownItem role="button" onClick={handleSignOut}>
                  {strings.signOut}
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>
          </CHeaderNav>
        </CContainer>
      </CHeader>
      {loading && <LoaderFullScreen />}
      {modalProfile.visible && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalProfile.visible}
          onClose={() => setModalProfile({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>{strings.profile}</CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <UserForm
                onSubmit={onProfileSubmit}
                type="update"
                initialValues={me}
                profile
                initialPhotoSrc={photoSrc}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
      {modalChangePass.visible && !me.authProvider && (
        <CModal
          alignment="center"
          visible={modalChangePass.visible}
          onClose={() => setModalChangePass({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>{strings.changePassword}</CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <AccountSettings.ChangePassword onSuccess={onSuccessChangePass} />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const styles = {
  containerFlex: { alignItems: "center", gap: 0 },
  logo: { width: "36px", marginRight: "10px" },
  header: { backgroundColor: "#14547b", color: "white" },
  brand: { color: "white" },
  navbarItem: { alignSelf: "center" },
  iconMenu: {
    cursor: "pointer",
    marginRight: "15px",
    transition:
      "margin-left .15s,margin-right .15s,box-shadow 75ms,transform .15s,width .15s,z-index 0s ease .15s",
  },
};

const strings = new LocalizedStrings({
  es: {
    headerTitle: "Cima IoT",
    settings: "Ajustes",
    profile: "Perfil",
    changePassword: "Cambiar contraseña",
    signOut: "Cerrar sesión",
    profileUpdated: "Perfil actualizado con éxito",
    passwordUpdated: "Contraseña actualizada con éxito",
  },
});

export default Header;
